import { useContext, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  classes,
  IconButton,
  Divider,
  Icon,
  Typography,
  styled,
  CircularProgress,
  TreeView,
  TreeItem,
} from 'bonfire-kindling';
import { useQueries } from '@tanstack/react-query';
import axios from 'helpers/axios';
import { useVendorTaxonomyCodes } from 'helpers/vendor';
import { ToastContext } from 'contexts';
import { TAXONOMYCODES } from 'constants/apis';
import { TAXONOMY_SETS } from 'constants/taxonomySets';

import DashboardOverviewContentCard from './DashboardOverviewContentCard';
const supportedCodeSets = [TAXONOMY_SETS.UNSPSC, TAXONOMY_SETS.NAICS];
const setTitles = {
  [TAXONOMY_SETS.UNSPSC]: 'UNSPSC',
  [TAXONOMY_SETS.NAICS]: 'NAICS',
};
const tokenizeCode = ({ token1, token2, token3, token4, token5, token6 }) =>
  [token1, token2, token3, token4, token5, token6].filter(Boolean).join('.');

const StyledTreeItem = styled(TreeItem)(() => ({
  [`& .${classes.components.MuiTreeItem.content} .${classes.components.MuiTreeItem.label}`]: {
    // Relative by default; need the root container to be relative parent to get full-width dividers
    position: 'static',
  },
}));
const FullWidthDivider = styled(Divider)(() => ({
  marginTop: '-1px',
  position: 'absolute',
  width: '100%',
  left: 0,
}));

const renderTree = treeNodes =>
  treeNodes.map(({ id, name, code, children }) => (
    <StyledTreeItem
      key={id}
      nodeId={id}
      label={
        <>
          <Typography variant="body2" my={2}>
            <strong>{name}</strong> {code}
          </Typography>
          <FullWidthDivider />
        </>
      }
    >
      {Array.isArray(children) ? renderTree(children) : null}
    </StyledTreeItem>
  ));

const CommodityCodes = () => {
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);
  const vendorCodeQuery = useVendorTaxonomyCodes();

  const [unspscTopCodeQuery, naicsTopCodeQuery] = useQueries({
    queries: supportedCodeSets.map(taxonomySetID => ({
      queryKey: ['taxonomyCodes', taxonomySetID],
      queryFn: () =>
        axios
          .get(TAXONOMYCODES, { params: { token2: '', taxonomySetID } })
          .then(({ data }) => data),
      onError: () => triggerToast({ message: t('generic_error') }),
      staleTime: 60 * 60 * 1000,
    })),
  });
  const filteredVendorCodes = useMemo(() => {
    if (!vendorCodeQuery.data) {
      return [];
    }
    return vendorCodeQuery.data.filter(({ taxonomySetID }) =>
      supportedCodeSets.includes(taxonomySetID)
    );
  }, [vendorCodeQuery.data]);

  const vendorCodeTree = useMemo(() => {
    if (!filteredVendorCodes || !unspscTopCodeQuery.data || !naicsTopCodeQuery.data) {
      return [];
    }
    const topCodes = unspscTopCodeQuery.data.concat(naicsTopCodeQuery.data);
    const vendorCodesGrouped = filteredVendorCodes.reduce((grouped, code) => {
      const { taxonomySetID, token1, token2 } = code;
      if (!supportedCodeSets.includes(taxonomySetID)) {
        return grouped;
      }
      if (!grouped[taxonomySetID]) {
        grouped[taxonomySetID] = {};
      }
      if (!grouped[taxonomySetID][token1]) {
        grouped[taxonomySetID][token1] = [];
      }
      if (token2) {
        grouped[taxonomySetID][token1].push(code);
      }
      return grouped;
    }, {});
    const topCodesGrouped = topCodes.reduce((grouped, code) => {
      if (!grouped[code.taxonomySetID]) {
        grouped[code.taxonomySetID] = {};
      }
      grouped[code.taxonomySetID][code.token1] = code;
      return grouped;
    }, {});
    const tree = Object.entries(vendorCodesGrouped).map(([id, codesForSetGrouped]) => ({
      id: `set${id}`,
      name: setTitles[id],
      children: Object.entries(codesForSetGrouped).map(([token1, subcodes]) => ({
        id: topCodesGrouped[id][token1].taxonomyCodeID,
        name: topCodesGrouped[id][token1].name,
        code: token1,
        children: subcodes.map(code => ({
          id: code.taxonomyCodeID,
          name: code.name,
          code: tokenizeCode(code),
        })),
      })),
    }));
    // Only one taxonomy set
    if (tree.length === 1) {
      return tree[0].children;
    }
    return tree;
  }, [filteredVendorCodes, unspscTopCodeQuery.data, naicsTopCodeQuery.data]);

  const isLoading =
    vendorCodeQuery.isLoading || unspscTopCodeQuery.isLoading || naicsTopCodeQuery.isLoading;

  return (
    <DashboardOverviewContentCard
      title={t('dashboard.overview.commodity_codes.title')}
      count={`(${filteredVendorCodes.length})`}
      iconName="code"
      link={
        <IconButton
          component={RouterLink}
          data-cy={'change-commodity-code'}
          to="/settings/commodity-codes"
          iconName="pencil-alt"
          aria-label={t('dashboard.overview.commodity_codes.edit')}
          variant="outline"
        />
      }
    >
      <Divider />
      <Box overflow="auto" position="relative" height="100%" display="flex">
        {isLoading ? (
          <Box sx={{ m: 'auto', p: { xs: 3, lg: 0 } }}>
            <CircularProgress aria-label={t('components.loading_icon')} />
          </Box>
        ) : vendorCodeTree.length ? (
          <TreeView
            disableSelection
            defaultCollapseIcon={<Icon iconName="chevron-down" color="primary" />}
            defaultExpanded={vendorCodeTree.map(({ id }) => id)}
            defaultExpandIcon={<Icon iconName="chevron-right" color="primary" />}
          >
            {renderTree(vendorCodeTree)}
          </TreeView>
        ) : (
          <Box sx={{ textAlign: 'center', m: 'auto', p: { xs: 3, lg: 0 } }}>
            <Typography component="p">{t('dashboard.overview.commodity_codes.empty')}</Typography>
          </Box>
        )}
      </Box>
    </DashboardOverviewContentCard>
  );
};

export default CommodityCodes;
